import { Select, type SelectProps } from "@mantine/core";
import { useCityCatalog, usePublicCityCatalog } from "../lib/useCityCatalog";

type Props = Omit<SelectProps, "data" | "filter" | "searchable" | "onChange" | "value"> & {
  value: string | null | undefined;
  onChange: (city: string) => void;
  /** Use public (unauthenticated) catalog — Apply / Join pages. */
  publicCatalog?: boolean;
  /** @deprecated Always flat "City - State"; kept for call-site compatibility. */
  flat?: boolean;
  /** When set, only these cities appear (e.g. cities that currently have jobs). */
  cities?: string[];
};

/**
 * Searchable India city picker. Flat "City - State" list; best matches float to the top.
 * Persists the catalog `label` string (city name, or "City, State" for name collisions).
 */
export function CitySelect({
  value,
  onChange,
  publicCatalog = false,
  flat: _flat = true,
  cities,
  label = "City",
  placeholder = "Search city…",
  nothingFoundMessage = "No matching cities",
  clearable = true,
  limit = 50,
  ...rest
}: Props) {
  const auth = useCityCatalog(!publicCatalog && cities == null);
  const pub = usePublicCityCatalog(!!publicCatalog && cities == null);
  const catalog = publicCatalog ? pub : auth;
  const data =
    cities != null
      ? cities.map((city) => ({ value: city, label: city }))
      : catalog.flatOptions;

  return (
    <Select
      label={label || undefined}
      placeholder={placeholder}
      searchable
      clearable={clearable}
      data={data}
      filter={cities != null ? undefined : catalog.filter}
      limit={limit}
      value={value || null}
      onChange={(v) => onChange(v ?? "")}
      nothingFoundMessage={nothingFoundMessage}
      {...rest}
    />
  );
}
